import {
  type NodeMatch,
  type RangeMode,
  type Node,
  type Location,
  Editor,
  Transforms,
} from 'slate';
import { type NodeInsertNodesOptions } from 'slate/dist/interfaces/transforms/node';
import { type TextInsertTextOptions } from 'slate/dist/interfaces/transforms/text';
import { ReactEditor } from 'slate-react';
import EditorUtils from './EditorUtils';
import AhaEditor from '../AhaEditor';

class TransformsUtils {
  static insertText(
    editor: Editor,
    text: string,
    options?: TextInsertTextOptions
  ) {
    Transforms.insertText(editor, text, options);
  }

  static insertNodes(
    editor: Editor,
    nodes: Node | Node[],
    options?: NodeInsertNodesOptions<Node>
  ) {
    Transforms.insertNodes(editor, nodes, options);
  }

  static removeNodes<T extends Node>(
    editor: Editor,
    options?: {
      at?: Location;
      match?: NodeMatch<T>;
      mode?: RangeMode;
      hanging?: boolean;
      voids?: boolean;
    }
  ) {
    return Transforms.removeNodes(editor, options);
  }

  // Delete all content from the editor.
  static deleteAll(editor: Editor) {
    Transforms.select(editor, {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    });
    Transforms.removeNodes(editor, {
      hanging: true,
      voids: true,
      mode: 'highest',
    });
    // Insert an empty document.
    this.insertNodes(editor, EditorUtils.emptyDocument().children);
    // Put selection at the end.
    Transforms.select(editor, {
      anchor: Editor.end(editor, []),
      focus: Editor.end(editor, []),
    });
  }

  static setNodeByElement<T extends Node>(
    editor: Editor,
    element: Node,
    props: Partial<T>
  ) {
    const path = ReactEditor.findPath(editor, element);
    return Transforms.setNodes(editor, props, { at: path });
  }

  static removeNodeByElement(editor: Editor, element: Node) {
    const path = ReactEditor.findPath(editor, element);
    return Transforms.removeNodes(editor, { at: path });
  }

  // Insert a character (e.g. \n) at the current selection including behaviors like
  // switching the block type. So we send a keydown event to the editor to
  // trigger all the plugin behaviors.
  static insertCharacter(
    editor: Editor,
    character: string,
    modifiers: KeyboardEventInit = {}
  ) {
    let key: KeyboardEvent['key'];

    if (character === '\n') {
      key = 'Enter';
    } else if (character >= 'A' && character <= 'Z') {
      key = character.toLowerCase();
      modifiers = { shiftKey: true };
    } else {
      key = character;
    }

    const ahaEditor = AhaEditor.getForEditor(editor);
    if (!ahaEditor) {
      throw new Error('AhaEditor not found for the editor');
    }
    const event = new KeyboardEvent('keydown', { key, ...modifiers });
    if (!ahaEditor.eventHandlers.onKeyDown(event)) {
      // If the event wasn't handled then insert the text manually.
      Editor.insertText(editor, character);
    }
  }
}

export default TransformsUtils;
